const CenterQuestion = require('./CenterQuestion');
const Child = require('./Child');
const Parent = require('./Parent');
const Program = require('./Program');

class WaitlistSpot {
  constructor() {
    this.init();
  }

  init() {
    this.id = undefined;
    this.list_id = undefined;
    this.child = undefined;
    this.parents = undefined;
    this.primary_contact = undefined;
    this.add_date = undefined;
    this.preferred_date = undefined;
    this.do_not_contact_before = undefined;
    this.specials = undefined;
    this.tour_id = undefined;
    this.state = undefined;
    this.removed = undefined;
    this.priority = undefined;
    this.comments = undefined;
    this.provider_notes = undefined;
    this.referral = undefined;
    this.seat_id = undefined;
    this.payment_id = undefined;
    this.custom_questions = undefined;
    this.custom_answers = undefined;
    this.programs = undefined;
    this.available_programs = undefined;

    // Read only properties
    this.tour_date = undefined;
    this.center_id = undefined;
  }

  copy() {
    const cp = new WaitlistSpot();

    cp.id = this.id;
    cp.list_id = this.list_id;
    cp.primary_contact = this.primary_contact;
    cp.add_date = this.add_date ? new Date(this.add_date) : undefined;
    cp.preferred_date = this.preferred_date ? new Date(this.preferred_date) : undefined;
    cp.do_not_contact_before = this.do_not_contact_before ? new Date(this.do_not_contact_before) : undefined;
    cp.specials = this.specials ? this.specials : undefined;
    cp.tour_date = this.tour_date ? new Date(this.tour_date) : undefined;
    cp.tour_id = this.tour_id;
    cp.center_id = this.center_id;
    cp.state = this.state;
    cp.priority = this.priority;
    cp.comments = this.comments;
    cp.provider_notes = this.provider_notes;
    cp.referral = this.referral;
    cp.seat_id = this.seat_id;
    cp.payment_id = this.payment_id;
    cp.removed = (this.removed) ? Object.assign({}, this.removed) : undefined;
    if (this.custom_answers) {
      cp.custom_answers = [];
      this.custom_answers.forEach((a) => {
        cp.custom_answers.push(Object.assign({}, a));
      });
    }

    if (this.child) {
      cp.child = this.child.copy();
    }
    if (this.parents) {
      cp.parents = [];
      this.parents.forEach((p) => {
        cp.parents.push(p.copy());
      });
    }
    if (this.programs) {
      cp.programs = [];
      this.programs.forEach((p) => {
        cp.programs.push(p.copy());
      })
    }
    if (this.available_programs) {
      cp.available_programs = [];
      this.available_programs.forEach((p) => {
        cp.available_programs.push(p.copy());
      })
    }
    if (this.custom_questions) {
      cp.custom_questions = [];
      this.custom_questions.forEach((q) => {
        cp.custom_questions.push(q.copy());
      });
    }

    return cp;
  }

  buildFromJSON(obj) {
    this.init();
    if (obj) {
      // Should we do validation on days, times, and question list?
      this.id = obj.id ? obj.id.toString() : undefined;
      this.list_id = obj.list_id ? obj.list_id.toString() : undefined;
      this.tour_id = obj.tour_id ? obj.tour_id.toString() : undefined;
      this.center_id = obj.center_id ? obj.center_id.toString() : undefined;
      this.primary_contact = obj.primary_contact ? obj.primary_contact.toString() : undefined;
      this.add_date = obj.add_date ? new Date(obj.add_date) : undefined;
      this.preferred_date = obj.preferred_date ? new Date(obj.preferred_date) : undefined;
      this.do_not_contact_before = obj.do_not_contact_before ? new Date(obj.do_not_contact_before) : undefined;
      this.specials = obj.specials ? obj.specials : undefined;
      this.tour_date = obj.tour_date ? new Date(obj.tour_date) : undefined;
      this.priority = obj.priority ? obj.priority : undefined;
      this.comments = obj.comments ? obj.comments : undefined;
      this.provider_notes = obj.provider_notes ? obj.provider_notes : undefined;
      this.referral = obj.referral ? obj.referral : undefined;
      this.seat_id = obj.seat_id ? obj.seat_id.toString() : undefined;
      this.payment_id = obj.payment_id ? obj.payment_id.toString() : undefined;
      this.state = obj.state && (['unpaid', 'active', 'hold', 'inactive', 'deleted', 'filled'].indexOf(obj.state) > -1)
        ? obj.state : undefined;

      if (obj.custom_answers) {
        this.custom_answers = [];
        obj.custom_answers.forEach((a) => {
          this.custom_answers.push({
            id: a.id ? a.id.toString() : undefined,
            answer: a.answer,  
          });
        });
      }
      if (obj.custom_questions) {
        this.custom_questions = [];
        obj.custom_questions.forEach((q) => {
          const question = new CenterQuestion();
          question.buildFromJSON(q);
          this.custom_questions.push(question);
        });
      }

      if (obj.child) {
        this.child = new Child();
        this.child.buildFromJSON(obj.child);
      } else if (obj.child_id) {
        this.child = new Child();
        this.child.setId(obj.child_id);
      }
      if (obj.parents) {
        this.parents = [];
        obj.parents.forEach((p) => {
          const parent = new Parent();
          parent.buildFromJSON(p);
          this.parents.push(parent);
        });
      }
      if (obj.programs) {
        this.programs = [];
        obj.programs.forEach((p) => {
          const program = new Program();
          program.buildFromJSON(p);
          this.programs.push(program);
        });
      }
      if (obj.available_programs) {
        this.available_programs = [];
        obj.available_programs.forEach((p) => {
          const program = new Program();
          program.buildFromJSON(p);
          this.available_programs.push(program);
        });
      }

      this.removed = obj.removed ? Object.assign({}, obj.removed) : undefined;
      if (obj.remove_date) {
        this.removed = this.removed || {};
        this.removed.removed = true;
        this.removed.date = obj.remove_date ? obj.remove_date : undefined;
        this.removed.reason = obj.removal_reason ? obj.removal_reason : '';
      }
    }
  }

  toString() {
    let result = '';
    
    if (this.id) {
      result = `ID ${this.id}: `;
    }
    if (this.list_id) {
      result += `List ${this.list_id}: `;
    }
    if (this.child) {
      result += `Child ${this.child.toString()}: `;
    }
    result += `with priority ${this.priority}`;
    return result;
  }

  getId() {
    return this.id;
  }

  setId(id) {
    this.id = id ? id.toString() : undefined;
  }

  getListId() {
    return this.list_id;
  }

  setListId(list_id) {
    this.list_id = list_id ? list_id.toString() : undefined;
  }

  getChild() {
    return this.child;
  }

  setChild(child) {
    this.child = (child) ? child.copy() : undefined;
  }

  getParents() {
    return this.parents;
  }

  setParents(parents) {
    this.parents = undefined;
    if (parents) {
      this.parents = [];
      parents.forEach((p) => {
        this.parents.push(p.copy());
      })
    }
  }

  getPrimaryContact() {
    return this.primary_contact;
  }

  setPrimaryContact(primary_contact) {
    this.primary_contact = primary_contact ? primary_contact.toString() : undefined;
  }

  getAddDate() {
    return this.add_date;
  }

  setAddDate(add_date) {
    this.add_date = add_date ? new Date(add_date) : undefined;
  }

  getDoNotContactBefore() {
    return this.do_not_contact_before;
  }

  setDoNotContactBefore(do_not_contact_before) {
    this.do_not_contact_before = do_not_contact_before ? new Date(do_not_contact_before) : undefined;
  }

  getPreferredDate() {
    return this.preferred_date;
  }

  setPreferredDate(preferred_date) {
    this.preferred_date = preferred_date ? new Date(preferred_date) : undefined;
  }

  getSpecials() {
    return this.specials;
  }

  setSpecials(specials) {
    this.specials = specials ? Object.assign({}, specials) : undefined;
  }

  getTourId() {
    return this.tour_id;
  }

  setTourId(tour_id) {
    this.tour_id = tour_id ? tour_id.toString() : undefined;
  }

  getState() {
    return this.state;
  }

  setState(state) {
    this.state = state && (['unpaid', 'active', 'hold', 'inactive', 'deleted', 'filled'].indexOf(state) > -1)
      ? state : undefined;
  }

  getPriority() {
    return this.priority;
  }
  
  setPriority(priority) {
    this.priority = priority;
  }

  getComments() {
    return this.comments;
  }

  setComments(comments) {
    this.comments = comments;
  }

  getProviderNotes() {
    return this.provider_notes;
  }

  setProviderNotes(provider_notes) {
    this.provider_notes = provider_notes;
  }
  
  getReferral() {
    return this.referral;
  }

  setReferral(referral) {
    this.referral = referral;
  }

  getSeatId() {
    return this.seat_id;
  }

  setSeatId(seat_id) {
    this.seat_id = seat_id ? seat_id.toString() : undefined;
  }

  getPaymentId() {
    return this.payment_id;
  }

  setPaymentId(payment_id) {
    this.payment_id = payment_id ? payment_id.toString() : undefined;
  }

  getRemoved() {
    return this.removed;
  }

  setRemoved(removed) {
    this.removecd = removed ? Object.assign({}, removed) : undefined;
  }

  getCustomQuestions() {
    return this.custom_questions;
  }

  setCustomQuestions(custom_questions) {
    this.custom_questions = undefined;
    if (custom_questions) {
      this.custom_questions = [];
      custom_questions.forEach((q) => {
        this.custom_questions.push(q.copy());
      });
    }
  }
  
  getCustomAnswers() {
    return this.custom_answers;
  }

  setCustomAnswers(custom_answers) {
    this.custom_answers = undefined;
    if (custom_answers) {
      this.custom_answers = [];
      custom_answers.forEach((a) => {
        this.custom_answers.push({
          id: a.id ? a.id.toString() : undefined,
          answer: a.answer,
        });
      });
    }
  }

  getPrograms() {
    return this.programs;
  }

  setPrograms(programs) {
    this.programs = undefined;
    if (programs) {
      this.programs = [];
      programs.forEach((p) => {
        this.programs.push(p.copy());
      })
    }
  }

  getAvailablePrograms() {
    return this.available_programs;
  }

  setAvailablePrograms(available_programs) {
    this.available_programs = undefined;
    if (available_programs) {
      this.available_programs = [];
      available_programs.forEach((p) => {
        this.available_programs.push(p.copy());
      })
    }
  }

  // Derived (read only)
  getTourDate() {
    return this.tour_date;
  }

  getCenterId() {
    return this.center_id;
  }

  // OK center ID really is derived, but to help when juggling spots across an array of centers
  // we'll allow the center ID to be set in the model
  setCenterId(center_id) {
    this.center_id = center_id ? center_id.toString() : undefined;
  }
}

module.exports = WaitlistSpot;
